import { HomepageQuery } from '@/generated/graphql';
import { routes } from '@/routes/routes';
import { CardItemSection } from '@/ui/components/cardItemSection/CardItemSection';
import { ContactSection } from '@/ui/components/contactSection/ContactSection';
import { Hero } from '@/ui/components/hero/Hero';
import { ItemCarousel } from '@/ui/components/itemCarousel/ItemCarousel';
import { Layout } from '@/ui/components/layout/Layout';
import { QuoteCarousel } from '@/ui/components/quoteCarousel/QuoteCarousel';
import { QuoteItem, TestimonialCarousel } from '@/ui/components/testimonialCarousel/TestimonialCarousel';
import { VideoTextSection } from '@/ui/components/videoTextSection/VideoTextSection';
import { graphql, useStaticQuery } from 'gatsby';
import * as React from 'react';
import * as styles from './HomePage.module.scss';
import { ProjectsFullWidthCarousel } from './components/projectsFullWidthCarousel/ProjectsFullWidthCarousel';

export const HomePage = () => {
  const homePageData: HomepageQuery = useStaticQuery(HOMEPAGE_QUERY);

  const solutionItems =
    homePageData.strapiHomepage?.solutionsCarousel?.map((s) => ({
      title: s?.title!,
      description: s?.description!,
      categoryTitle: s?.type?.title!,
      mediaUrl: s?.video?.url!,
      isVideo: true,
      detailsLink: routes.solutions,
    })) || [];

  const projectsCarouselItems = homePageData.strapiHomepage?.featuredCaseStudies?.map((cs) => ({
    id: cs?.id!,
    title: cs?.title!,
    altText: cs?.hero?.alternativeText,
    gatsbyImage: cs?.hero?.localFile?.childImageSharp?.gatsbyImageData,
    tags: cs?.solutions?.map((s) => s?.type?.title!) || [],
    subtitle: cs?.subtitle!,
  }));

  const articles = homePageData.allStrapiIdea.nodes.map((a) => ({
    id: a.slug!,
    link: routes.idea({ slug: a.slug! }),
    altText: a.mainImage?.alternativeText,
    gatsbyImage: a.mainImage?.localFile?.childImageSharp?.gatsbyImageData,
    title: a.title!,
    date: a.date,
    tags: a.ideaTags?.map((t) => t?.name!) || [],
  }));

  const testimonials = (homePageData.strapiHomepage?.employees?.filter((e): e is NonNullable<typeof e> => !!e) ||
    []) as QuoteItem[];
  const quotes =
    homePageData.strapiHomepage
      ?.clientTestimonials!.filter((x) => !!x)
      .map((x) => ({ testimonial: x?.testimonial!, clientDescription: x?.clientDescription! })) || [];

  const sectorItems =
    homePageData.allStrapiSector?.nodes?.map((s) => {
      const mediaUrl = s?.sectorExpertiseBlockMedia?.url || s?.heroBackgroundVideo?.url || s?.heroBackgroundImage?.url;

      const isVideo =
        !!mediaUrl &&
        (mediaUrl.endsWith('.mp4') ||
          mediaUrl.endsWith('.webm') ||
          mediaUrl.endsWith('.mov') ||
          (s?.heroBackgroundVideo?.url && s?.heroBackgroundVideo?.url === mediaUrl));

      return {
        title: s?.header!,
        description: s?.description!,
        categoryTitle: s?.sector_type?.title!,
        mediaUrl,
        isVideo,
        detailsLink: routes.singleSector({ title: s?.sector_type?.title! }),
      };
    }) || [];

  return (
    <Layout>
      <Hero
        texts={homePageData.strapiHomepage?.heroTexts?.map((x) => x?.name!) || []}
        className={styles.homepage__topHero}
      />
      {!!homePageData.strapiHomepage?.videoTextSection && (
        <VideoTextSection
          title={homePageData.strapiHomepage.videoTextSection?.title || ''}
          vimeoUrl={homePageData.strapiHomepage.videoTextSection?.VideoUrl || ''}
          videoUrl={homePageData.strapiHomepage.videoTextSection?.video?.url || ''}
          imageUrl={homePageData.strapiHomepage.videoTextSection?.image?.url || ''}
          CTAName={homePageData.strapiHomepage.videoTextSection?.CTAname || ''}
          CTALink={homePageData.strapiHomepage.videoTextSection?.CTALink || ''}
          description={homePageData.strapiHomepage.videoTextSection?.description as any}
          className={styles.homepage__videoTextSection}
        />
      )}
      {projectsCarouselItems && projectsCarouselItems.length > 0 && (
        <ProjectsFullWidthCarousel items={projectsCarouselItems} className={styles.homepage__fullWidthCarousel} />
      )}
      {solutionItems.length > 0 && (
        <ItemCarousel
          items={solutionItems}
          className={styles.homepage__solutions}
          linkText="See all solutions"
          allItemsLink={routes.solutions}
        />
      )}
      {!!homePageData.strapiHomepage?.historyVideoTextSection && (
        <VideoTextSection
          title={homePageData.strapiHomepage.historyVideoTextSection?.title || ''}
          vimeoUrl={homePageData.strapiHomepage.historyVideoTextSection?.VideoUrl || ''}
          videoUrl={homePageData.strapiHomepage.historyVideoTextSection?.video?.url || ''}
          imageUrl={homePageData.strapiHomepage.historyVideoTextSection?.image?.url || ''}
          CTAName={homePageData.strapiHomepage.historyVideoTextSection?.CTAname || ''}
          CTALink={homePageData.strapiHomepage.historyVideoTextSection?.CTALink || ''}
          description={homePageData.strapiHomepage.historyVideoTextSection?.description as any}
          className={styles.homepage__videoTextSection}
        />
      )}
      {testimonials && testimonials.length > 0 && (
        <TestimonialCarousel items={testimonials} className={styles.homepage__testimonialCarousel} />
      )}
      {sectorItems.length > 0 && (
        <ItemCarousel items={sectorItems} className={styles.homepage__sectors} linkText="See sector details" />
      )}
      {articles.length > 0 && (
        <CardItemSection
          title="Latest ideas"
          allToLink={routes.ideas}
          allToText="See all articles"
          className={styles.homepage__ideas}
          posts={articles}
        />
      )}
      {quotes && quotes.length > 0 && <QuoteCarousel title="Client testimonials" quotes={quotes} />}
      <ContactSection />
    </Layout>
  );
};

const HOMEPAGE_QUERY = graphql`
  query Homepage {
    allStrapiIdea(sort: { date: DESC }, limit: 3) {
      nodes {
        slug
        title
        date
        updatedAt
        ideaTags {
          name
        }
        mainImage {
          alternativeText
          url
          localFile {
            childImageSharp {
              gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED, width: 1920, quality: 90)
            }
          }
        }
      }
    }
    allStrapiSector {
      nodes {
        id
        header
        description
        sector_type {
          title
        }
        heroBackgroundImage {
          url
        }
        heroBackgroundVideo {
          url
        }
        sectorExpertiseBlockMedia {
          url
        }
      }
    }
    strapiHomepage {
      heroTexts {
        name
      }
      videoTextSection {
        title
        VideoUrl
        video {
          url
        }
        image {
          url
        }
        description {
          type
          level
          children {
            type
            text
            bold
          }
        }
        CTAname
        CTALink
      }
      historyVideoTextSection {
        title
        VideoUrl
        video {
          url
        }
        image {
          url
        }
        description {
          type
          level
          children {
            type
            text
            bold
          }
        }
        CTAname
        CTALink
      }
      featuredCaseStudies {
        id
        title
        subtitle
        hero {
          url
          alternativeText
          localFile {
            childImageSharp {
              gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED, width: 1920)
            }
          }
        }
        solutions {
          type {
            title
          }
        }
      }
      employees {
        name
        quote
        yearsAtCompany
        position
        video {
          url
        }
      }
      clientTestimonials {
        testimonial
        clientDescription
      }
      solutionsCarousel {
        type {
          title
        }
        title
        description
        video {
          url
        }
      }
    }
  }
`;
